import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import Icon from '../components/Icon';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const STATUS_OPTIONS = ['scheduled', 'completed', 'cancelled', 'no_show'];

function ClientAutocomplete({ clients, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Initialise query from existing value
  useEffect(() => {
    if (value) {
      const c = clients.find(c => String(c.id) === String(value));
      if (c) setQuery(c.first_name + ' ' + c.last_name);
    } else {
      setQuery('');
    }
  }, [value, clients]);

  // Close dropdown on outside click
  useEffect(() => {
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const suggestions = query.length > 0
    ? clients.filter(c => (c.first_name + ' ' + c.last_name).toLowerCase().includes(query.toLowerCase())).slice(0, 8)
    : clients.slice(0, 8);

  function select(client) {
    setQuery(client.first_name + ' ' + client.last_name);
    onChange(client.id);
    setOpen(false);
  }

  function clear() {
    setQuery('');
    onChange('');
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Icon name="icon-search.png" size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        <input
          className="form-input"
          style={{ paddingLeft: 38, paddingRight: value ? 36 : 14 }}
          placeholder="Search clients..."
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); onChange(''); }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        {value && (
          <button onClick={clear} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-text-muted)', fontSize: 16, lineHeight: 1 }}>×</button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, zIndex: 300, maxHeight: 220, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
          {!value && query === '' && (
            <div style={{ padding: '8px 14px 4px', fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>All Clients</div>
          )}
          {suggestions.map(c => (
            <button key={c.id} onMouseDown={() => select(c)}
              style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '11px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, transition: 'background 0.1s', fontFamily: 'var(--font)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <img src="/icons/icon-user.png" alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', background: 'var(--color-surface-2)', flexShrink: 0 }} onError={(e) => e.target.style.display='none'} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-text)' }}>{c.first_name} {c.last_name}</div>
                {c.email && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{c.email}</div>}
              </div>
            </button>
          ))}
          <button onMouseDown={clear}
            style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderTop: '1px solid var(--color-border)', padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-muted)', fontFamily: 'var(--font)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            No client
          </button>
        </div>
      )}
    </div>
  );
}

function AppointmentModal({ date, appointment, clients, onSave, onDelete, onClose }) {
  const isEdit = !!appointment;
  const [form, setForm] = useState({
    client_id: appointment?.client_id || '',
    start_time: appointment?.start_time ? new Date(appointment.start_time).toISOString().slice(0,16) : (date ? date + 'T09:00' : ''),
    end_time: appointment?.end_time ? new Date(appointment.end_time).toISOString().slice(0,16) : (date ? date + 'T10:00' : ''),
    status: appointment?.status || 'scheduled',
    location: appointment?.location || '',
    notes: appointment?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      alert(err.message);
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 500, display: 'flex', alignItems: 'flex-end' }}
      onClick={onClose}>
      <div style={{ width: '100%', background: 'var(--color-surface)', borderRadius: '16px 16px 0 0', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{isEdit ? 'Edit Appointment' : 'New Appointment'}</div>
          <button className="icon-btn" onClick={onClose}>
            <Icon name="icon-close.png" alt="Close" />
          </button>
        </div>
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label className="form-label">Client</label>
            <ClientAutocomplete
              clients={clients}
              value={form.client_id}
              onChange={id => setForm(f => ({ ...f, client_id: id }))}
            />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="icon-clock.png" size={14} />
              Start Time
            </label>
            <input className="form-input" type="datetime-local" value={form.start_time}
              onChange={e => setForm(f => ({...f, start_time: e.target.value}))} required />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="icon-clock.png" size={14} />
              End Time
            </label>
            <input className="form-input" type="datetime-local" value={form.end_time}
              onChange={e => setForm(f => ({...f, end_time: e.target.value}))} required />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="icon-location.png" size={14} />
              Location
            </label>
            <input className="form-input" value={form.location}
              onChange={e => setForm(f => ({...f, location: e.target.value}))} placeholder="e.g. Gym, Online, Home" />
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} />
          </div>
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Appointment'}
          </button>
          {isEdit && (
            <button type="button" onClick={() => { onDelete(appointment.id); onClose(); }}
              style={{ width: '100%', marginTop: 10, minHeight: 48, background: 'rgba(215,38,56,0.1)', border: '1px solid rgba(215,38,56,0.3)', color: 'var(--color-primary)', borderRadius: 'var(--radius)', fontFamily: 'var(--font)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
              Delete Appointment
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

export default function Schedule() {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [modal, setModal] = useState(null);
  const [view, setView] = useState('month');

  useEffect(() => {
    api.get('/appointments').then(setAppointments);
    api.get('/clients').then(setClients);
  }, []);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  function aptsForDay(day) {
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return appointments.filter(a => a.start_time.startsWith(dateStr));
  }

  async function handleSave(form, existingId) {
    if (existingId) {
      const updated = await api.put('/appointments/' + existingId, form);
      setAppointments(prev => prev.map(a => a.id === updated.id ? updated : a));
    } else {
      const created = await api.post('/appointments', form);
      setAppointments(prev => [...prev, created]);
    }
  }

  async function handleDelete(id) {
    await api.delete('/appointments/' + id);
    setAppointments(prev => prev.filter(a => a.id !== id));
  }

  const statusColors = { scheduled: '#3b82f6', completed: '#2ECC71', cancelled: '#666', no_show: '#F39C12' };

  const upcomingApts = appointments
    .filter(a => new Date(a.start_time) >= today && a.status === 'scheduled')
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
    .slice(0, 10);

  return (
    <div>
      {modal && (
        <AppointmentModal
          date={modal.date}
          appointment={modal.appointment}
          clients={clients}
          onSave={(form) => handleSave(form, modal.appointment?.id)}
          onDelete={handleDelete}
          onClose={() => setModal(null)}
        />
      )}

      <div className="page-header">
        <h1>
          <Icon name="icon-calendar.png" />
          Schedule
        </h1>
        <button className="icon-btn" onClick={() => setModal({ date: new Date().toISOString().split('T')[0] })}>
          <Icon name="icon-add.png" alt="Add" />
        </button>
      </div>

      <div className="page-content">
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['month', 'upcoming'].map(v => (
            <button key={v} onClick={() => setView(v)}
              style={{ flex: 1, minHeight: 40, borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', fontWeight: 600, fontSize: 14, textTransform: 'capitalize',
                background: view === v ? 'var(--color-primary)' : 'var(--color-surface-2)',
                color: view === v ? '#fff' : 'var(--color-text-muted)' }}>
              {v === 'upcoming' ? 'Upcoming' : 'Month'}
            </button>
          ))}
        </div>

        {view === 'month' && (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <button className="icon-btn" onClick={() => setCurrentDate(new Date(year, month - 1, 1))}>
                <Icon name="icon-back.png" alt="Prev" />
              </button>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{MONTHS[month]} {year}</div>
              <button className="icon-btn" onClick={() => setCurrentDate(new Date(year, month + 1, 1))} style={{ transform: 'rotate(180deg)' }}>
                <Icon name="icon-back.png" alt="Next" />
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
              {DAYS.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 600, padding: '4px 0' }}>{d}</div>)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
              {Array.from({ length: firstDay }).map((_, i) => <div key={'e'+i} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
                const apts = aptsForDay(day);
                const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                return (
                  <div key={day} onClick={() => setModal({ date: dateStr })}
                    style={{ minHeight: 48, padding: 4, borderRadius: 6, cursor: 'pointer',
                      background: isToday ? 'rgba(215,38,56,0.15)' : 'transparent',
                      border: isToday ? '1px solid rgba(215,38,56,0.4)' : '1px solid transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = isToday ? 'rgba(215,38,56,0.2)' : 'var(--color-surface-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = isToday ? 'rgba(215,38,56,0.15)' : 'transparent'}>
                    <div style={{ fontSize: 13, fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--color-primary)' : 'var(--color-text)', marginBottom: 2 }}>{day}</div>
                    {apts.slice(0, 2).map(a => (
                      <div key={a.id} onClick={e => { e.stopPropagation(); setModal({ appointment: a }); }}
                        style={{ width: '100%', height: 4, borderRadius: 2, background: statusColors[a.status] || '#3b82f6', marginBottom: 2 }} />
                    ))}
                    {apts.length > 2 && <div style={{ fontSize: 9, color: 'var(--color-text-muted)' }}>+{apts.length-2}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === 'upcoming' && (
          upcomingApts.length === 0 ? (
            <div className="empty-state"><p>No upcoming appointments</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {upcomingApts.map(a => {
                const start = new Date(a.start_time);
                const end = new Date(a.end_time);
                return (
                  <div key={a.id} className="card" style={{ cursor: 'pointer', borderLeft: '3px solid ' + (statusColors[a.status] || '#3b82f6') }}
                    onClick={() => setModal({ appointment: a })}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {a.first_name ? a.first_name + ' ' + a.last_name : 'No client'}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Icon name="icon-clock.png" size={12} />
                      {start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    {a.location && (
                      <div style={{ fontSize: 13, color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                        <Icon name="icon-location.png" size={12} />
                        {a.location}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}
